import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Container, Skeleton, Stack, IconButton } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";

import "swiper/css";

import { ref, get } from "firebase/database";
import { rtdb } from "../services/firebase";
import ProductCard from "./ProductCard";

const arrowBtnSx = {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 2,
    bgcolor: "background.paper",
    boxShadow: "0 2px 12px rgba(0,0,0,0.12)",
    border: "1px solid",
    borderColor: "divider",
    "&:hover": { bgcolor: "action.hover" },
    "&.Mui-disabled": { opacity: 0.35 },
};

const TopSellersCarousel = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [swiperInstance, setSwiperInstance] = useState(null);
    const [navState, setNavState] = useState({
        locked: true,
        isBeginning: true,
        isEnd: true,
    });

    const syncNavigation = useCallback((swiper) => {
        if (!swiper) return;
        setNavState({
            locked: Boolean(swiper.isLocked),
            isBeginning: swiper.isBeginning,
            isEnd: swiper.isEnd,
        });
    }, []);

    useEffect(() => {
        carregarTopSales();
    }, []);

    useEffect(() => {
        if (!swiperInstance || products.length === 0) return;
        swiperInstance.update();
        syncNavigation(swiperInstance);
    }, [products, swiperInstance, syncNavigation]);

    async function carregarTopSales() {
        try {
            const appSnap = await get(ref(rtdb, "app/topSales"));
            if (!appSnap.exists()) return setProducts([]);

            const ids = appSnap.val();
            if (!Array.isArray(ids) || ids.length === 0) return setProducts([]);

            const requests = ids.map((id) =>
                get(ref(rtdb, `products/${id}`))
            );

            const results = await Promise.all(requests);

            const loadedProducts = results
                .map((snap, index) =>
                    snap.exists() ? { id: ids[index], ...snap.val() } : null
                )
                .filter(Boolean);

            setProducts(loadedProducts);
        } catch (err) {
            console.error("Erro ao carregar Top Sellers:", err);
        } finally {
            setLoading(false);
        }
    }

    const breakpoints = {
        0: {
            slidesPerView: 1.05,
            spaceBetween: 14,
        },
        600: {
            slidesPerView: 2.2,
            spaceBetween: 16,
        },
        900: {
            slidesPerView: 3,
            spaceBetween: 18,
        },
        1200: {
            slidesPerView: 4,
            spaceBetween: 16,
        },
    };

    const LoadingSkeletons = () => (
        <Stack direction="row" spacing={2} sx={{ overflow: "hidden", pl: 1 }}>
            {[1, 2, 3].map((item) => (
                <Box key={item} sx={{ minWidth: 260 }}>
                    <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
                    <Skeleton width="70%" height={24} sx={{ mt: 1 }} />
                    <Skeleton width="40%" height={22} />
                </Box>
            ))}
        </Stack>
    );

    if (!loading && products.length === 0) return null;

    return (
        <Container
            maxWidth={false}
            sx={{
                maxWidth: {
                    md: "1100px",
                    lg: "1180px",
                    xl: "1180px",
                },
                mb: { xs: 20, md: 12 }
            }}
        >
            {/* HEADER */}
            <Box
                sx={{
                    mb: { xs: 2.5, md: 4 },
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                }}
            >
                <Box
                    sx={{
                        width: 5,
                        height: 26,
                        bgcolor: "var(--azul)",
                        borderRadius: 1,
                    }}
                />
                <Typography
                    sx={{
                        fontWeight: 900,
                        fontSize: { xs: 20, md: 28 },
                        color: "var(--cinza)",
                        letterSpacing: "-0.5px",
                    }}
                >
                    Mais Vendidos
                </Typography>
            </Box>

            {loading ? (
                <LoadingSkeletons />
            ) : (
                <Box
                    sx={{
                        position: "relative",
                        pl: { xs: 5, sm: 6, md: 7 },
                        pr: { xs: 5, sm: 6, md: 7 },
                        mx: { xs: -1, sm: 0 },
                    }}
                >
                    {!navState.locked && (
                        <>
                            <IconButton
                                aria-label="Produtos anteriores"
                                onClick={() => swiperInstance?.slidePrev()}
                                disabled={navState.isBeginning}
                                sx={{
                                    ...arrowBtnSx,
                                    left: { xs: 0, sm: 4, md: 8 },
                                    width: { xs: 36, sm: 40 },
                                    height: { xs: 36, sm: 40 },
                                }}
                            >
                                <ChevronLeftIcon sx={{ fontSize: { xs: 22, sm: 28 } }} />
                            </IconButton>
                            <IconButton
                                aria-label="Próximos produtos"
                                onClick={() => swiperInstance?.slideNext()}
                                disabled={navState.isEnd}
                                sx={{
                                    ...arrowBtnSx,
                                    right: { xs: 0, sm: 4, md: 8 },
                                    width: { xs: 36, sm: 40 },
                                    height: { xs: 36, sm: 40 },
                                }}
                            >
                                <ChevronRightIcon sx={{ fontSize: { xs: 22, sm: 28 } }} />
                            </IconButton>
                        </>
                    )}

                    <Swiper
                        modules={[Autoplay]}
                        watchOverflow={true}
                        breakpoints={breakpoints}
                        onSwiper={(swiper) => {
                            setSwiperInstance(swiper);
                            syncNavigation(swiper);
                        }}
                        onSlideChange={(swiper) => syncNavigation(swiper)}
                        onResize={(swiper) => syncNavigation(swiper)}
                        onBreakpoint={(swiper) => syncNavigation(swiper)}
                        autoplay={{
                            delay: 5000,
                            disableOnInteraction: false,
                            pauseOnMouseEnter: true,
                        }}
                        style={{
                            paddingBottom: 10,
                            paddingTop: 8,
                            marginLeft: "auto",
                            marginRight: "auto",
                        }}
                    >
                        {products.map((product) => (
                            <SwiperSlide
                                key={product.id}
                                style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    marginRight: 0,
                                    marginLeft: -10,
                                }}
                            >
                                <Box sx={{ width: "100%", maxWidth: 260 }}>
                                    <ProductCard product={product} />
                                </Box>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </Box>
            )}
        </Container>
    );
};

export default TopSellersCarousel;